/**
 * commentsService.ts
 *
 * The seam between the Comments_Panel and wherever comments are stored.
 *
 * No comments API exists yet (design.md, Backend gaps 1; task 12.3 checked the
 * GraphQL schema, resolvers, infrastructure and frontend/src/graphql and found
 * no comment type, query, mutation or subscription). The panel therefore runs
 * on an in-memory store behind this small interface, and only when the
 * `VITE_COMMENTS_ENABLED` flag is on. The flag is unset by default, so users
 * never see comments that vanish on reload.
 *
 * Swap point: when the backend ships, write an API-backed `CommentsService`
 * and install it with `setCommentsService` before the Workspace renders (for
 * example in app bootstrap, when `isCommentsEnabled()`). The panel, hook and
 * model do not change. Keep `createInMemoryCommentsService` as the test double.
 *
 * What the backend needs to provide (design.md, Backend gaps 1), and how it
 * maps onto this model:
 * - A per-job (job, loan or search) list of entries, oldest first, that
 *   persists for the life of the job (Requirement 12.5).
 * - User comment: id, author, timestamp, text, `anchor_type`
 *   (mode | field | finding | exception), `anchor_id`, and an anchor label
 *   -> `UserComment` with `CommentAnchor { type, id, label }`.
 * - Loan and document context (`loan`, `doc`) on the anchor, so selecting a
 *   field or exception restores the right selection. Optional.
 * - System events (for example "Analysis re-run"): id, timestamp, text, and
 *   an optional anchor -> `SystemEvent`.
 * - A way to learn about new entries: a subscription if available, otherwise
 *   poll `list` -> `subscribe`.
 * GraphQL operation names are intentionally not guessed here. Add them to
 * frontend/src/graphql once the schema exists.
 *
 * Requirements: 12.1, 12.2, 12.3, 12.5.
 */

import {
  sortStream,
  type CommentAnchor,
  type CommentStreamEntry,
  type SystemEvent,
  type UserComment,
} from './commentsModel';

export interface NewComment {
  author: string;
  text: string;
  anchor: CommentAnchor;
}

export interface CommentsService {
  /** Comments and system events for one job, loan or search, oldest first. */
  list(itemId: string): Promise<CommentStreamEntry[]>;
  add(itemId: string, input: NewComment): Promise<UserComment>;
  /** Called after the stream for `itemId` changes. Returns an unsubscribe function. */
  subscribe(itemId: string, onChange: () => void): () => void;
}

/** Feature flag: the Comments toggle is hidden unless it is on. */
export function isCommentsEnabled(): boolean {
  return import.meta.env.VITE_COMMENTS_ENABLED === 'true';
}

export interface InMemoryCommentsService extends CommentsService {
  /** Add a system event to the stream (used until the backend emits them). */
  addEvent(itemId: string, text: string, anchor?: CommentAnchor): SystemEvent;
}

export function createInMemoryCommentsService(
  now: () => Date = () => new Date()
): InMemoryCommentsService {
  const streams = new Map<string, CommentStreamEntry[]>();
  const listeners = new Map<string, Set<() => void>>();
  let sequence = 0;

  const nextId = () => `local-${++sequence}`;
  const notify = (itemId: string) => listeners.get(itemId)?.forEach((fn) => fn());
  const push = (itemId: string, entry: CommentStreamEntry) => {
    streams.set(itemId, [...(streams.get(itemId) ?? []), entry]);
    notify(itemId);
  };

  return {
    async list(itemId) {
      return sortStream(streams.get(itemId) ?? []);
    },
    async add(itemId, input) {
      const comment: UserComment = {
        kind: 'comment',
        id: nextId(),
        author: input.author,
        createdAt: now().toISOString(),
        anchor: input.anchor,
        text: input.text,
      };
      push(itemId, comment);
      return comment;
    },
    subscribe(itemId, onChange) {
      const set = listeners.get(itemId) ?? new Set<() => void>();
      set.add(onChange);
      listeners.set(itemId, set);
      return () => {
        set.delete(onChange);
      };
    },
    addEvent(itemId, text, anchor) {
      const event: SystemEvent = {
        kind: 'event',
        id: nextId(),
        createdAt: now().toISOString(),
        text,
        anchor,
      };
      push(itemId, event);
      return event;
    },
  };
}

let activeService: CommentsService = createInMemoryCommentsService();

export function getCommentsService(): CommentsService {
  return activeService;
}

/** Replace the store, for example with the API-backed one (task 12.3). */
export function setCommentsService(service: CommentsService): void {
  activeService = service;
}
